import { ApplicationHelpType } from '../../../utilities/Constants';
import { ConfidenceBadge } from './ConfidenceBadge';
import { DisplayMessages } from '../../../utilities/DisplayMessages';
import { Help } from '../../shared/Help';
import { LoaderAnimation } from '../../shared/Loader';
import PredictedOutcomeChart from '../../report/charts/PredictedOutcomeChart';
import { Stack } from 'react-bootstrap';
import { useCaseFileContext } from '../../../contexts/useCaseFileContext';
import { useGetPredictedOutcome } from './data/useGetPredictedOutcome';

export function PredictedOutcome(props: {
    confidenceScore?: number | undefined;
    isReady: boolean;
}) {
    const caseFile = useCaseFileContext();
    const { data, isLoading, isError } = useGetPredictedOutcome(
        props.isReady,
        caseFile.currentCase.guid
    );

    if (isLoading) {
        return <LoaderAnimation size="xs" variant="secondary" />;
    }

    if (isError) {
        return <div className="text-danger">{DisplayMessages.UnexpectedError}</div>;
    }

    const confidenceHelpText = `Checkmate ML Confidence measures how well the model’s predictions correlate with actual case resolution values.  We assume that how the models perform in cross-validation is very similar to how they perform on cases where the outcome is not yet known.`;
    const improveConfidenceHelpText = `The following variables are currently missing in this Case File.  If these missing variables were updated, confidence would improve.  The missing factors are listed in order of having the biggest impact on the confidence score.`;

    return (
        <div className="row">
            <div className="col-sm-6 col-lg-6 col-md-12">
                <div>
                    <div className="bold mb-2 mr-1" style={{ borderBottom: '1px solid #c7c8c9' }}>
                        How criteria affected the Predicted Outcome
                    </div>
                    {data ? (
                        <div className="mb-4" style={{ position: 'relative' }}>
                            <PredictedOutcomeChart
                                data={[...data.effects].sort((a, b) =>
                                    a.displayName < b.displayName ? -1 : 1
                                )}
                            />
                        </div>
                    ) : (
                        <>No Data</>
                    )}
                </div>
                <Stack gap={3}>
                    <Stack
                        direction="horizontal"
                        style={{ alignItems: 'center', columnGap: '1rem' }}
                    >
                        <div>
                            <strong>Confidence</strong>{' '}
                            <Help
                                helpText={confidenceHelpText}
                                title="Confidence"
                                type={ApplicationHelpType.Info}
                            />
                        </div>
                        <div>
                            <ConfidenceBadge
                                confidenceRating={props.confidenceScore}
                                confidenceLevel={data?.confidenceColorLevel}
                            />
                        </div>
                    </Stack>
                    <div>
                        <div
                            className="bold mb-2 mr-1"
                            style={{ borderBottom: '1px solid #c7c8c9' }}
                        >
                            Additional criteria that would improve confidence
                            <Help
                                helpText={improveConfidenceHelpText}
                                title="Additional criteria that would improve confidence"
                                type={ApplicationHelpType.Info}
                            />
                        </div>
                        <Stack direction="vertical" gap={1}>
                            {data?.missingFactors.map((item, idx) => (
                                <span key={idx}>{item}</span>
                            ))}
                        </Stack>
                    </div>
                </Stack>
            </div>
        </div>
    );
}
